import React from "react";
import { Link } from "react-router-dom";

/**
 * Renders a chat message body with @mentions highlighted.
 * - Mentions of the current viewer render in hot magenta (#FF3BFF).
 * - Mentions of anyone else render in acid green (#D4FF00).
 * - Each @handle is a clickable link to the mentioned user's profile.
 *
 * A mention only counts as "real" when the server confirmed it in msg.mentions,
 * so random @strings that don't match a user still render as plain text.
 */
const HANDLE_RE = /@([A-Za-z0-9_]{3,24})/g;

export default function MentionText({ body, mentions, myUsername, className, style }) {
  if (!body) return null;

  // Build a lowercased set of confirmed handles for O(1) lookup.
  const confirmed = new Set(
    (mentions || []).map((m) => String(m.username || "").toLowerCase())
  );
  const meLc = (myUsername || "").toLowerCase();

  const parts = [];
  let last = 0;
  let match;
  HANDLE_RE.lastIndex = 0;
  while ((match = HANDLE_RE.exec(body)) !== null) {
    const handle = match[1];
    const start = match.index;
    const end = start + match[0].length;
    if (start > last) parts.push(body.slice(last, start));
    if (confirmed.has(handle.toLowerCase())) {
      const isMe = handle.toLowerCase() === meLc;
      parts.push(
        <Link
          key={`m-${start}`}
          to={`/u/${handle}`}
          data-testid={`mention-${handle}`}
          className={`font-mono font-semibold px-0.5 rounded-sm ${
            isMe
              ? "text-hotpink bg-hotpink/10 border-b border-hotpink"
              : "text-acid hover:underline"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          @{handle}
        </Link>
      );
    } else {
      // Not a confirmed user — render as plain text so we don't lie about link targets.
      parts.push(match[0]);
    }
    last = end;
  }
  if (last < body.length) parts.push(body.slice(last));

  return (
    <span className={className} style={style}>
      {parts}
    </span>
  );
}
